import React, { useEffect } from 'react';

import ActionButton from '~components/atoms/ActionButton';
import Table from '~components/molecules/Table';
import UtilFunctions from '~utils/functions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { DiscountStatusEnum, ModuleEnum, PermissionsEnum, TransactionActionEnum } from '~utils/enums';

import * as Styles from './_discountsTableStyles.module.scss';
import combinedActions from '~redux/combinedActions';
import { TDiscountDetailsSettings } from '~types/feat/setting';

const DiscountTable = () => {
    const dispatch = useAppDispatch();

    const {
        Misc:
        {
            dateTimeFormat,
            renderBinaryStatusChip,
        },
    } = UtilFunctions;

    const discountListingIndex = useAppSelector(state => state.setting.discounts.index);
    const allDiscounts = useAppSelector(state => state.setting.discounts.data);
    const access = useAppSelector(state => state.auth.userDetails.access);
    const hasEditAccess = access.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.Discount);
    const isLoading = useAppSelector(state => state.setting.loadings.getDiscounts);

    const setSelectedDiscountSettingDetails = (data:TDiscountDetailsSettings) => dispatch(combinedActions.settingSetDiscountSettingDetails(data));

    useEffect(() => {
        dispatch(combinedActions.settingGetDiscountsAttempt());
    }, [discountListingIndex]);

    const tableHeader = [
        'DISCOUNT CODE',
        'DISCOUNT NAME',
        'AMOUNT',
        'DISCOUNT TYPE',
        'STATUS',
        'DISCOUNT START AT',
        'DISCOUNT END AT',
        'ACTION',
    ];

    const tableBody = allDiscounts.map(item => {
        const {
            discountCode,
            discountName,
            amount,
            percentageAmount,
            transactionType,
            status,
            discountStartDate,
            discountEndDate,
        } = item;

        const handleClickViewDiscount = () => {
            dispatch(combinedActions.settingSetProductManagementDiscountDetailsModalIsOpen(true));
            setSelectedDiscountSettingDetails(item);
        };

        const handleClickEditDiscount = () => {
            dispatch(combinedActions.settingSetProductManagementDiscountDetailsModalIsEditing(true));
            dispatch(combinedActions.settingSetProductManagementDiscountDetailsModalIsOpen(true));
            setSelectedDiscountSettingDetails(item);
        };

        const handleClickDeleteDiscount = () => {
            dispatch(combinedActions.settingSetProductManagementDiscountDeleteModalIsOpen(true));
            setSelectedDiscountSettingDetails(item);
        };

        const renderActionButton = () => {
            return (
                <div
                    className={Styles['button-container']}
                >
                    <ActionButton
                        label='View'
                        onClick={() => handleClickViewDiscount()}
                    />

                    {hasEditAccess
                        && (
                            <ActionButton
                                label='Edit'
                                onClick={() => handleClickEditDiscount()}
                            />
                        )
                    }

                    {(hasEditAccess && item.status !== DiscountStatusEnum.Deactivated)
                        && (
                            <ActionButton
                                label='Delete'
                                onClick={() => handleClickDeleteDiscount()}
                                className={Styles.danger}
                            />
                        )
                    }
                </div>
            );
        };

        const renderTransactionType = () => {
            if (transactionType === TransactionActionEnum['Bank Sell']){
                return (
                    <div>
                        <p>Discount</p>
                        <p>({TransactionActionEnum[transactionType]})</p>
                    </div>
                );
            }

            if (transactionType === TransactionActionEnum['Bank Buy']){
                return (
                    <div>
                        <p>Markup</p>
                        <p>({TransactionActionEnum[transactionType]})</p>
                    </div>
                );
            }
        };

        const getAmount = (fixedValue: number | undefined, percentageValue: number | undefined) => {
            if (fixedValue){
                const shifted = ((fixedValue * 100) / 100);

                if (Number.isInteger(shifted * 100)) {
                    return `RM${shifted.toFixed(2)}`;
                }

                return `RM${parseFloat(shifted.toFixed(6)).toString()}`;
            }

            if (percentageValue){
                const shifted = percentageValue * 100;

                if (Number.isInteger(shifted * 100)) {
                    return shifted.toFixed(2).concat('%');
                }

                return parseFloat(shifted.toFixed(6)).toString().concat('%');
            }

            return '-';
        };

        return [
            discountCode,
            discountName,
            getAmount(amount, percentageAmount),
            renderTransactionType(),
            renderBinaryStatusChip(status, DiscountStatusEnum[status]) || '-',
            dateTimeFormat(discountStartDate),
            dateTimeFormat(discountEndDate || ''),
            renderActionButton(),
        ];
    });

    return (
        <Table
            header={tableHeader}
            body={tableBody}
            isLoading={isLoading}
        />
    );
};

export default DiscountTable;
